import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const modes = [
  {
    key: 'default',
    label: 'Desktop',
    icon: '🖥️',
    description: 'Full desktop layout',
    color: 'blue'
  },
  {
    key: 'touch',
    label: 'Touch',
    icon: '📱',
    description: 'Tablet / touch optimized',
    color: 'purple'
  },
  {
    key: 'console',
    label: 'Console',
    icon: '🎛️',
    description: 'ROSS-style broadcast switcher',
    color: 'amber'
  },
  {
    key: 'broadcast',
    label: 'Broadcast',
    icon: '📡',
    description: 'Hardware-style broadcast panel',
    color: 'emerald'
  },
  {
    key: 'broadcast-min',
    label: 'iPad',
    icon: '📲',
    description: 'Touch-safe with action modal',
    color: 'teal'
  }
];

const colorClasses = {
  blue: {
    activeBg: 'bg-blue-600',
    activeText: 'text-white',
    hoverBg: 'hover:bg-blue-50',
    ring: 'ring-blue-500',
    dot: 'bg-blue-500'
  },
  purple: {
    activeBg: 'bg-purple-600',
    activeText: 'text-white',
    hoverBg: 'hover:bg-purple-50',
    ring: 'ring-purple-500',
    dot: 'bg-purple-500'
  },
  amber: {
    activeBg: 'bg-amber-600',
    activeText: 'text-white',
    hoverBg: 'hover:bg-amber-50',
    ring: 'ring-amber-500',
    dot: 'bg-amber-500'
  },
  emerald: {
    activeBg: 'bg-emerald-600',
    activeText: 'text-white',
    hoverBg: 'hover:bg-emerald-50',
    ring: 'ring-emerald-500',
    dot: 'bg-emerald-500'
  },
  teal: {
    activeBg: 'bg-teal-600',
    activeText: 'text-white',
    hoverBg: 'hover:bg-teal-50',
    ring: 'ring-teal-500',
    dot: 'bg-teal-500'
  }
};

export const OperatorModeSelector = ({ currentMode, matchId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleModeChange = (mode) => {
    localStorage.setItem('operatorMode', mode);
    setOpen(false);
    if (mode === 'default') {
      navigate(`/live/${matchId}`);
    } else {
      navigate(`/live/${matchId}?mode=${mode}`);
    }
  };

  const current = modes.find(m => m.key === currentMode) || modes[0];
  const c = colorClasses[current.color];
  const isConsole = currentMode === 'console';

  return (
    <div className="relative z-50" ref={ref}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition-all ${
          isConsole
            ? `border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 ${open ? 'ring-2 ring-amber-500' : ''}`
            : `border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm ${open ? `ring-2 ${c.ring}` : ''}`
        }`}
      >
        <span className="text-lg">{current.icon}</span>
        <span className="text-sm font-semibold">{current.label}</span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''} ${isConsole ? 'text-gray-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`absolute right-0 top-full mt-2 w-64 rounded-xl shadow-xl border z-50 overflow-hidden ${
          isConsole
            ? 'bg-gray-800 border-gray-600'
            : 'bg-white border-gray-200'
        }`}>
          <div className={`px-3 py-2 border-b ${isConsole ? 'border-gray-700' : 'border-gray-100'}`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${isConsole ? 'text-gray-500' : 'text-gray-400'}`}>
              Operator Mode
            </span>
          </div>
          {modes.map((mode) => {
            const isActive = mode.key === currentMode;
            const mc = colorClasses[mode.color];
            return (
              <button
                key={mode.key}
                onClick={() => handleModeChange(mode.key)}
                className={`w-full flex items-center gap-3 px-3 py-3 transition-colors text-left ${
                  isActive
                    ? isConsole
                      ? `${mc.activeBg} ${mc.activeText}`
                      : `${mc.activeBg} ${mc.activeText}`
                    : isConsole
                      ? 'text-gray-300 hover:bg-gray-700'
                      : `text-gray-700 ${mc.hoverBg}`
                }`}
              >
                <span className="text-2xl flex-shrink-0">{mode.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{mode.label}</span>
                    {isActive && (
                      <span className={`w-2 h-2 rounded-full ${isActive && !isConsole ? 'bg-white' : mc.dot}`} />
                    )}
                  </div>
                  <span className={`text-xs ${
                    isActive
                      ? 'opacity-80'
                      : isConsole ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {mode.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
